import { Alert, Box, Stack } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { IRecordEditItemEmployment, RecordEditItemEmployment } from "source/models/record/recordEditItemEmployment";
import * as recordService from "source/models/services/recordService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { List } from "source/components/framework/List/List";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { EllipsisTypography } from "source/components/framework/Typography/EllipsisTypography";
import { RecordEditListFirstCol } from "../RecordEditListFirstCol";
import { RecordUserEmploymentDialog } from "./RecordUserEmploymentDialog";
import CircleIcon from "@mui/icons-material/Circle";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

// RecordEditEmploymentList
export interface IRecordEditEmploymentListProp {
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    userCode: string;
    jobTitles: string[];
    employments: IRecordEditItemEmployment[];
    attachments: IFileAttachment[];
    onRemoved: (ids: string[]) => void;
    onSaved: (employment: IRecordEditItemEmployment) => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

interface IRecordEditEmploymentListState {
    // List
    isLoading: boolean;
    items: IRecordEditItemEmployment[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;

    // Dialog
    item: IRecordEditItemEmployment;
    showEditDialog: boolean;
}

export class RecordEditEmploymentList extends React.Component<IRecordEditEmploymentListProp, IRecordEditEmploymentListState> {
    getFilteredList = (filter: string, column: string, asc: boolean): IRecordEditItemEmployment[] => {
        const props = this.props;
        let result = props.employments ? props.employments.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.companyName.toLowerCase().indexOf(lowFilter) > -1 || i.jobTitle.toLowerCase().indexOf(lowFilter) > -1 || i.comment.toLowerCase().indexOf(lowFilter) > -1);
        }
        RecordEditItemEmployment.sortRecordEditItemEmployments(result, column, asc);
        return result;
    };

    constructor(props: IRecordEditEmploymentListProp) {
        super(props);
        const employments = this.getFilteredList("", "startDate", false);
        this.state = {
            isLoading: false,
            items: employments,
            filter: "",
            sortColumn: "startDate",
            sortOrderIsAsc: false,
            selectedId: null,
            checkedIds: [],
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            item: null,
            showEditDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const items = this.getFilteredList(filter, column, asc);
        const item = this.state.showEditDialog && this.state.item
            ? items.find(i => i.id === this.state.item.id)
            : null;
        const index = item ? items.findIndex(i => i.id === item.id) : -1;
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            items,
            item,
            showEditDialog: !!item,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < items.length - 1,
        });
    };

    componentDidUpdate(prevProps: IRecordEditEmploymentListProp, prevState: IRecordEditEmploymentListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.employments) === EditItem.getHash(props.employments)) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IRecordEditEmploymentListProp, recordEmploymentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", recordEmploymentIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const employment = state.items.find(i => i.id === selectedIds[0]);
        if (!employment) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordUserEmploymentParameter, employment.getUiTitle()), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(this.props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditRemoveEmployments(saveData.obj));
            if (!result) return;
            obj.props.onRemoved(selectedIds);
        }
    };
    
    handleAdd = () => {
        const editItem = new RecordEditItemEmployment();
        editItem.id = Base.getGuid();
        editItem.startDate = Base.getNowUtcDate().addDays(-1).getTime();
        this.setState({
            item: editItem,
            showEditDialog: true
        });
    };

    handleOpen = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            item: state.items[index],
            selectedId: id,
            showEditDialog: true,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    onRecordUserEmploymentDialogSaved = async (employment: IRecordEditItemEmployment, closeDialog: boolean) => {
        if (!employment) return;
        this.setState({
            showEditDialog: !closeDialog,
            selectedId: employment.id,
            item: employment
        });
        this.props.onSaved(employment);
    };

    onRecordUserEmploymentDialogClose = () => {
        this.setState({
            showEditDialog: false
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            item: state.items[index],
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const currentEmployments = state.items.filter(i => Base.isNullOrUndefined(i.endDate));
        return (
            <>
                <VerticalContainer
                    before={<>
                        <FormField title={Translations.CurrentWorkPlace} margin={0}>
                            {currentEmployments.length < 0 &&
                                <></>
                            }
                            <Stack direction="row" flexWrap="nowrap">
                                {currentEmployments.map((employment, index) =>
                                    <Box key={employment.id} display="flex" alignItems="center">
                                        <EllipsisTypography key={employment.id}>{(employment.companyName ? employment.companyName + ", " : "") + Translations.EmploymentStartDate + ": " + Base.utcTimeToDateStr(employment.startDate)}</EllipsisTypography>
                                        {!(currentEmployments.length === 1 || index === currentEmployments.length - 1) && (
                                            <CircleIcon sx={{ color: "action.selected", fontSize: "1rem", margin: "0 0.5rem" }} />
                                        )}
                                    </Box>
                                )}
                            </Stack>
                        </FormField>
                        <FormField title={Translations.Employments} />
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddEmployment}
                                    removeTooltip={Translations.RemoveEmployment}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info">{Translations.NewRecordUserMustBeSavedBeforeEmploymentsCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                globalLoadingInUse={false}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 2, column: "startDate", content: Translations.EmploymentStartDate }, // "col-xl-1 col-2"
                                        { size: 2, column: "endDate", content: Translations.EmploymentEndDate }, // "col-xl-1 col-2"
                                        { size: 2, column: "company", content: Translations.Company }, // "col-xl-2 col-2"
                                        { size: 2, column: "jobTitle", content: Translations.JobTitle }, // "col-xl-2 col-2"
                                        { size: 4, column: "comment", content: Translations.AdditionalInformation } // "col-xl-6 col-4"
                                    ]
                                }}
                                lines={
                                    state.items.map((employment) => {
                                        return {
                                            columns: [
                                                {
                                                    size: 2,
                                                    content:
                                                        <RecordEditListFirstCol
                                                            id={employment.id}
                                                            code={Base.utcTimeToDateStr(employment.startDate)}
                                                            attachments={props.attachments}
                                                            onClick={this.handleOpen}
                                                        />
                                                },
                                                { size: 2, content: Base.utcTimeToDateStr(employment.endDate) },
                                                { size: 2, content: employment.companyName },
                                                { size: 2, content: employment.jobTitle },
                                                { size: 4, content: employment.comment }
                                            ],
                                            id: employment.id
                                        };
                                    })
                                }
                                noDataMessage={Translations.AddEmployments}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                                onOpenLine={this.handleOpen}
                            />
                        }
                    </>}
                />
                {state.showEditDialog &&
                    <RecordUserEmploymentDialog
                        companyId={props.companyId}
                        recordId={props.recordId}
                        userCode={props.userCode}
                        item={state.item}
                        attachments={props.attachments}
                        jobTitles={props.jobTitles}
                        hasPrevious={state.selectedItemHasPrevious}
                        hasNext={state.selectedItemHasNext}
                        onSaved={this.onRecordUserEmploymentDialogSaved}
                        onClose={this.onRecordUserEmploymentDialogClose}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onAttachmentsRemoved={props.onAttachmentsRemoved}
                        onAttachmentsAdded={props.onAttachmentsAdded}
                    />
                }
            </>
        );
    }
}